/**
 * State + selection rules for the SKU category facet in the sidebar.
 *
 * The rows are ordinary facet checkboxes inside #CollectionFiltersForm — `filter.p.m.custom.rc_cat_l2`
 * / `…_l3` where those metafield filters are enabled, `filter.p.tag` where they are not — so the
 * theme's collection-filters-form.js already handles submitting, AJAX and re-rendering. Two things
 * it cannot do for us:
 *
 * 1. Checked state. The snippet renders it server-side from the metafield filter's active_values,
 *    but the tag fallback has no reliable server-side state: a filter returns at most 100 values
 *    and this store has far more tags than that, so the selected one is often missing from
 *    `results.filters` (and Liquid has no request.query_string). We re-apply it from the URL.
 * 2. Hierarchy. Values of one metafield filter are OR-ed, but multiple filter.p.tag values are
 *    ANDed, so under the fallback two selected siblings would always return zero products. We keep
 *    one selection per level and clear level 3 rows that no longer belong to the selected level 2.
 *
 * Level 3 rows are always rendered (hidden) so revealing them needs no server round-trip.
 *
 * IMPORTANT — why applyState only runs on freshly rendered markup:
 * the theme submits on `input` debounced by 500ms and reads the form when that timer fires. An
 * earlier version re-ran applyState from any DOM mutation, so a click was reverted to the URL's
 * (pre-click) state within a frame and the submit then saw no change at all — clicking a category
 * appeared to do nothing while pasting the same filtered URL worked. Server-rendered rows arrive
 * without the `data-sku-cat-applied` marker, which is how we tell a real re-render from our own
 * edits and never clobber a selection that has not been submitted yet.
 */
(function () {
  'use strict';

  const ROW = 'input[data-sku-cat-level]';
  const PENDING = ROW + ':not([data-sku-cat-applied])';
  const FORM = '#CollectionFiltersForm';

  /** Mirror the URL onto the checkboxes, reveal the selected L2's children, open touched groups. */
  function applyState(root) {
    const rows = Array.prototype.slice.call(root.querySelectorAll(ROW));
    if (!rows.length) return;

    // Read by the row's own param name: rows submit to filter.p.m.custom.rc_cat_l2/l3 where those
    // metafield filters exist and fall back to filter.p.tag where they do not.
    const params = new URLSearchParams(window.location.search);

    rows.forEach(function (input) {
      input.checked = params.getAll(input.name).indexOf(input.value) !== -1;
      input.setAttribute('data-sku-cat-applied', '');
    });

    // A level 3 list is only visible while its own level 2 checkbox is checked.
    root.querySelectorAll('[data-sku-cat-children]').forEach(function (list) {
      const item = list.closest('li');
      const parent = item && item.querySelector('input[data-sku-cat-level="2"]');
      const show = !!(parent && parent.checked);
      list.hidden = !show;
      if (!show) {
        list.querySelectorAll(ROW).forEach(function (child) {
          child.checked = false;
        });
      }
    });

    // Open any group that has a selection; leave the merchant's default alone otherwise.
    root.querySelectorAll('.sku-category-block').forEach(function (block) {
      if (!block.querySelector(ROW + ':checked')) return;
      block.classList.add('is-current');
      const heading = block.querySelector('.sidebarBlock-heading');
      if (heading) heading.classList.add('is-clicked');
      const wrapper = block.querySelector('.sidebarBlock-contentWrapper');
      if (wrapper) wrapper.style.display = 'block';
    });
  }

  /**
   * Drop the selections that cannot coexist with the one just made, then let the theme's own
   * debounced submit pick up the corrected form. Unchecking programmatically fires no events, so
   * this causes no extra submits.
   */
  document.addEventListener('change', function (event) {
    const input = event.target;
    if (!input || !input.matches || !input.matches(ROW)) return;

    const form = input.closest('form');
    if (!form) return;

    const level = input.getAttribute('data-sku-cat-level');
    const scope = input.getAttribute('data-sku-cat-scope');

    form.querySelectorAll(ROW).forEach(function (other) {
      if (other === input) return;
      const otherLevel = other.getAttribute('data-sku-cat-level');
      const otherScope = other.getAttribute('data-sku-cat-scope');

      // Same level under the same parent: a sibling, which cannot stay selected.
      if (otherLevel === level && otherScope === scope) {
        other.checked = false;
        return;
      }
      // Level 3 only makes sense under the level 2 that is still selected.
      if (level === '2' && otherLevel === '3' && (!input.checked || otherScope !== input.id)) {
        other.checked = false;
      }
    });

    if (level === '2') {
      const item = input.closest('li');
      const list = item && item.querySelector('[data-sku-cat-children]');
      if (list) list.hidden = !input.checked;
    }
  });

  function init() {
    const form = document.querySelector(FORM);
    if (!form) return;

    applyState(form);

    // The theme swaps facet markup in place on AJAX filter reloads. Only the rows it just rendered
    // are missing the marker, so this reacts to real re-renders and ignores our own edits.
    let queued = false;
    const observer = new MutationObserver(function () {
      if (queued || !form.querySelector(PENDING)) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        applyState(form);
      });
    });
    observer.observe(form, { childList: true, subtree: true });

    // Back / forward navigation changes the URL without re-rendering.
    window.addEventListener('popstate', function () {
      form.querySelectorAll(ROW).forEach(function (input) {
        input.removeAttribute('data-sku-cat-applied');
      });
      applyState(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
